import { ComponentCO2, ComponentCO2Link } from '@acies/domain';
import { ObservableCollection } from '@acies/observables';
import { InputFieldEvent } from '@acies/ui';
import { TranslateService } from '@ngx-translate/core';
import AuthorizationError from '@shared/errors/authorization-error';
import ConflictError from '@shared/errors/conflict-error';
import InternalServerError from '@shared/errors/internal-server-error';
import NetworkError from '@shared/errors/network-error';
import ParseError from '@shared/errors/parse-error';
import UnknownError from '@shared/errors/unknown-error';
import { Subscription } from 'rxjs';
import ComponentLink from '../domain/component-link';
import SearchCO2LinkResult from '../domain/search-co2link-result';
import CO2Model from '../models/co2-model';
import SearchCO2LinkModel from '../models/search-co2link.model';
import CO2Service from '../services/co2-service';

type SearchResults = ParseError | SearchCO2LinkResult | NetworkError | AuthorizationError | ConflictError | InternalServerError | UnknownError;

export default class SearchCo2LinkController {
	public dialog: HTMLDialogElement | null;
	#service: CO2Service;
	#searchSubscription: Subscription | null;
	#co2Collection: ObservableCollection<ComponentCO2>;
	#searchCO2LinkModel: SearchCO2LinkModel;
	#translateService: TranslateService;
	public constructor(model: CO2Model, service: CO2Service, translateService: TranslateService) {
		this.dialog = null;
		this.#service = service;
		this.#searchSubscription = null;
		this.#searchCO2LinkModel = model.searchCO2LinkModel;
		this.#co2Collection = model.listComponentCO2Model.co2Collection;
		this.#translateService = translateService;
		this.searchSubscriptionHandler = this.searchSubscriptionHandler.bind(this);
	}

	public showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
		}
	}

	public searchInputChanged(event: Event): void {
		if (event instanceof InputFieldEvent) {
			this.#setDefaultIsSearchingModelProperties();
			// if we have an in flight search request,
			// we unsubscribe and cancel the request
			if (this.#searchSubscription) {
				this.#searchSubscription.unsubscribe();
			}
			// only call the API if we have something to search for
			if (event.text.length > 0) {
				this.#searchSubscription = this.#service.searchCO2Link(event.text).subscribe(this.searchSubscriptionHandler);
			} else {
				this.#searchCO2LinkModel.searchSpinnerVisible = false;
				this.#searchCO2LinkModel.searchLabelText = '';
			}
		}
	}

	#setDefaultIsSearchingModelProperties(): void {
		this.#searchCO2LinkModel.searchResults = [];
		this.#searchCO2LinkModel.searchSpinnerVisible = true;
		this.#searchCO2LinkModel.searchLabelVariant = 'default';
		this.#searchCO2LinkModel.searchLabelText = this.#translateService.instant('masterdata.co2.searching');
	}

	private searchSubscriptionHandler(value: SearchResults): void {
		this.#searchCO2LinkModel.searchSpinnerVisible = false;
		if (value instanceof SearchCO2LinkResult) {
			this.#setSearchResultValuesInModel(value);
		} else {
			this.#setSearchErrorValuesInModel(value);
		}
	}

	#setSearchErrorValuesInModel(value: Exclude<SearchResults, SearchCO2LinkResult>): void {
		this.#searchCO2LinkModel.searchLabelVariant = 'error';
		const type = value instanceof NetworkError ? 'network' : 'unknown';
		this.#searchCO2LinkModel.searchLabelText = this.#translateService.instant(`masterdata.co2.errors.${type}`);
	}

	#setSearchResultValuesInModel(value: SearchCO2LinkResult): void {
		this.#searchCO2LinkModel.searchResults = this.#transformCO2LinkToComponentLink(value.items);
		const total = value.total;
		if (total > 0) {
			if (total > 100) {
				this.#searchCO2LinkModel.searchLabelText = `${this.#translateService.instant('masterdata.co2.search_result')}${total}`;
			} else {
				this.#searchCO2LinkModel.searchLabelText = this.#translateService.instant('masterdata.co2.found', { total });
			}
		} else {
			this.#searchCO2LinkModel.searchLabelText = this.#translateService.instant('masterdata.co2.no_results');
		}
	}

	#transformCO2LinkToComponentLink(value: ComponentCO2Link[]): ComponentLink[] {
		const links: ComponentLink[] = [];
		for (const co2Link of value) {
			links.push(this.#getComponentLink(co2Link));
		}
		return links;
	}

	#getComponentLink(value: ComponentCO2Link): ComponentLink {
		const co2s = this.#co2Collection.collection;
		for (const co2 of co2s) {
			if (value.co2 === co2.self) {
				return new ComponentLink({ component: co2.epd, link: value.name });
			}
		}
		return new ComponentLink({ component: '', link: value.name });
	}
}
